import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

// Card-backed GTD tickler panel. Items live as cards on the dedicated Tickler board
// (/api/tickler). Next 30 days render day by day; everything further out condenses
// into month groups. Items can be completed, rescheduled, or promoted onto a live
// board right from here. Footer exposes the iCal subscription feed.

const MS_DAY = 24 * 60 * 60 * 1000;

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const parseIso = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TicklerPanel = ({ token, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [boards, setBoards] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('');
  const [adding, setAdding] = useState(false);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [promoteId, setPromoteId] = useState(null);
  const [copied, setCopied] = useState(false);

  const headers = { headers: { 'x-auth-token': token } };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/tickler', headers);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not load the tickler.');
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get('/api/boards', headers)
      .then(res => setBoards((res.data || []).filter(b => !b.isArchived && b.slug !== 'tickler')))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newDate) return;
    setAdding(true);
    try {
      await axios.post('/api/tickler', { title: newTitle.trim(), date: newDate }, headers);
      setNewTitle('');
      setNewDate('');
      await load();
    } catch (err) {
      alert(err.response?.data?.msg || 'Could not add the item.');
    } finally {
      setAdding(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await axios.put(`/api/tickler/${id}`, { done: true }, headers);
      setData(d => ({ ...d, items: d.items.filter(i => i._id !== id) }));
    } catch (err) {
      alert(err.response?.data?.msg || 'Could not complete the item.');
    }
  };

  const handleReschedule = async (id) => {
    if (!rescheduleDate) return;
    try {
      await axios.put(`/api/tickler/${id}`, { date: rescheduleDate }, headers);
      setRescheduleId(null);
      setRescheduleDate('');
      await load();
    } catch (err) {
      alert(err.response?.data?.msg || 'Could not reschedule the item.');
    }
  };

  const handlePromote = async (id, boardId) => {
    if (!boardId) return;
    try {
      const res = await axios.post(`/api/tickler/${id}/promote`, { boardId }, headers);
      setPromoteId(null);
      setData(d => ({ ...d, items: d.items.filter(i => i._id !== id) }));
      const target = boards.find(b => b._id === boardId);
      if (target) {
        // Brief confirmation, no toast system in the app
        console.log(`Promoted to ${target.name} (${res.data.column})`);
      }
    } catch (err) {
      alert(err.response?.data?.msg || 'Could not send the item to that board.');
    }
  };

  const copyFeed = () => {
    if (!data?.feedUrl) return;
    navigator.clipboard.writeText(data.feedUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const ItemRow = ({ item, showDot }) => (
    <div className="group flex items-start gap-2 py-1">
      <button
        onClick={() => handleComplete(item._id)}
        title="Mark done"
        className="mt-0.5 w-4 h-4 flex-shrink-0 rounded-full border-2 border-gray-300 dark:border-gray-500 hover:border-green-500 hover:bg-green-500/20 transition-colors"
      />
      <div className="flex-1 min-w-0">
        <span className="text-sm text-gray-800 dark:text-gray-200 leading-snug">{item.title}</span>
        {item.labels?.length > 0 && (
          <span className="ml-2 inline-flex gap-1 align-middle">
            {item.labels.map((l, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: l.color }}>{l.text}</span>
            ))}
          </span>
        )}
        {rescheduleId === item._id && (
          <span className="ml-2 inline-flex items-center gap-1">
            <input
              type="date"
              value={rescheduleDate}
              onChange={e => setRescheduleDate(e.target.value)}
              className="text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-1 py-0.5"
            />
            <button onClick={() => handleReschedule(item._id)} className="text-xs font-bold text-blue-600 dark:text-blue-400">Save</button>
            <button onClick={() => { setRescheduleId(null); setRescheduleDate(''); }} className="text-xs text-gray-400">Cancel</button>
          </span>
        )}
        {promoteId === item._id && (
          <span className="ml-2 inline-flex items-center gap-1">
            <select
              defaultValue=""
              onChange={e => handlePromote(item._id, e.target.value)}
              className="text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-1 py-0.5 max-w-[180px]"
            >
              <option value="" disabled>Send to board…</option>
              {boards.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
            </select>
            <button onClick={() => setPromoteId(null)} className="text-xs text-gray-400">Cancel</button>
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        <button
          onClick={() => { setRescheduleId(item._id === rescheduleId ? null : item._id); setRescheduleDate(item.date || ''); setPromoteId(null); }}
          title="Reschedule"
          className="p-1 text-gray-400 hover:text-blue-500"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
        </button>
        <button
          onClick={() => { setPromoteId(item._id === promoteId ? null : item._id); setRescheduleId(null); }}
          title="Send to a board"
          className="p-1 text-gray-400 hover:text-teal-500"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
        </button>
      </div>
    </div>
  );

  // Group items: overdue / next 30 days (day by day) / later (by month)
  const grouped = (() => {
    if (!data?.items) return null;
    const today = parseIso(todayIso());
    const horizon = new Date(today.getTime() + 30 * MS_DAY);
    const overdue = [];
    const byDay = new Map();
    const byMonth = new Map();
    for (const item of data.items) {
      if (!item.date) continue;
      const d = parseIso(item.date);
      if (d < today) overdue.push(item);
      else if (d <= horizon) {
        if (!byDay.has(item.date)) byDay.set(item.date, []);
        byDay.get(item.date).push(item);
      } else {
        const key = item.date.slice(0, 7);
        if (!byMonth.has(key)) byMonth.set(key, []);
        byMonth.get(key).push(item);
      }
    }
    return {
      overdue,
      days: [...byDay.entries()].sort((a, b) => a[0].localeCompare(b[0])),
      months: [...byMonth.entries()].sort((a, b) => a[0].localeCompare(b[0])),
      horizon,
    };
  })();

  const fmtHorizon = grouped ? `${MONTHS_SHORT[grouped.horizon.getMonth()]} ${grouped.horizon.getDate()}` : '';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[88vh] flex flex-col overflow-hidden">

          {/* Header */}
          <div className="px-6 pt-5 pb-4 bg-gradient-to-r from-teal-500/10 to-blue-500/10 dark:from-teal-500/20 dark:to-blue-500/20 border-b border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">Tickler</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Dated reminders, surfaced on their day. Each item is a card.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {data?.boardSlug && (
                  <Link to={`/boards/${data.boardSlug}`} className="text-xs font-bold text-teal-600 dark:text-teal-400 hover:underline" title="Open the Tickler board">
                    Open board
                  </Link>
                )}
                <button onClick={load} title="Refresh" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                </button>
                <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
              </div>
            </div>

            {/* Quick add */}
            <form onSubmit={handleAdd} className="mt-4 flex gap-2">
              <input
                type="text"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="Tickle me about…"
                className="flex-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg px-3 py-1.5 focus:ring-teal-500 focus:border-teal-500"
              />
              <input
                type="date"
                value={newDate}
                onChange={e => setNewDate(e.target.value)}
                className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded-lg px-2 py-1.5"
              />
              <button
                type="submit"
                disabled={adding || !newTitle.trim() || !newDate}
                className="px-4 py-1.5 text-sm font-bold text-white bg-gradient-to-r from-teal-500 to-blue-600 rounded-lg shadow hover:opacity-90 disabled:opacity-40 transition-opacity"
              >
                Add
              </button>
            </form>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading && <div className="py-10 text-center text-gray-500 dark:text-gray-400 text-sm">Loading tickler…</div>}
            {error && !loading && <div className="py-6 text-center text-red-600 dark:text-red-400 text-sm">{error}</div>}

            {!loading && !error && grouped && (
              <>
                {grouped.overdue.length === 0 && grouped.days.length === 0 && grouped.months.length === 0 && (
                  <div className="py-10 text-center text-gray-500 dark:text-gray-400 text-sm">No tickler items. You're clear.</div>
                )}

                {/* Overdue */}
                {grouped.overdue.length > 0 && (
                  <div className="mb-5 rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-900/20 px-3 py-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-red-600 dark:text-red-400 mb-1">Overdue ({grouped.overdue.length})</div>
                    {grouped.overdue.map(item => (
                      <div key={item._id} className="flex items-center gap-2">
                        <span className="text-xs font-bold text-red-500 w-12 flex-shrink-0">
                          {(() => { const d = parseIso(item.date); return `${d.getMonth() + 1}/${d.getDate()}`; })()}
                        </span>
                        <div className="flex-1"><ItemRow item={item} /></div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Next 30 days, day by day */}
                {grouped.days.length > 0 && (
                  <div className="mb-6">
                    <div className="text-xs font-bold uppercase tracking-wide text-teal-600 dark:text-teal-400 mb-2">
                      Next 30 Days <span className="text-gray-400 dark:text-gray-500 normal-case font-normal">(through {fmtHorizon})</span>
                    </div>
                    <div className="space-y-2">
                      {grouped.days.map(([iso, items]) => {
                        const d = parseIso(iso);
                        const isToday = iso === todayIso();
                        return (
                          <div key={iso} className="flex gap-3">
                            <div className={`flex flex-col items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 border ${isToday ? 'bg-gradient-to-br from-teal-500 to-blue-600 text-white border-transparent shadow-md' : 'bg-gray-50 dark:bg-gray-700/60 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300'}`}>
                              <span className={`text-[9px] font-bold uppercase ${isToday ? 'text-teal-100' : 'text-gray-400 dark:text-gray-500'}`}>{isToday ? 'Today' : WEEKDAYS[d.getDay()]}</span>
                              <span className="text-base font-black leading-none">{d.getDate()}</span>
                              <span className={`text-[9px] font-bold uppercase ${isToday ? 'text-teal-100' : 'text-gray-400 dark:text-gray-500'}`}>{MONTHS_SHORT[d.getMonth()]}</span>
                            </div>
                            <div className="flex-1 min-w-0 border-b border-gray-100 dark:border-gray-700/60 pb-2">
                              {items.map(item => <ItemRow key={item._id} item={item} />)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Later, condensed by month */}
                {grouped.months.map(([key, items]) => {
                  const [y, m] = key.split('-').map(Number);
                  return (
                    <div key={key} className="mb-4">
                      <div className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5 border-b border-gray-200 dark:border-gray-700 pb-1">
                        {MONTHS[m - 1]} {y}
                      </div>
                      {items.map(item => (
                        <div key={item._id} className="flex items-center gap-2">
                          <span className="text-xs font-bold text-gray-400 dark:text-gray-500 w-8 flex-shrink-0 text-right">
                            {parseIso(item.date).getDate()}
                          </span>
                          <div className="flex-1"><ItemRow item={item} /></div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Footer: iCal feed */}
          {data?.feedUrl && (
            <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              <input
                readOnly
                value={data.feedUrl}
                onFocus={e => e.target.select()}
                className="flex-1 text-[11px] text-gray-500 dark:text-gray-400 bg-transparent border-0 focus:ring-0 truncate"
                title="Subscribe to this URL in Google Calendar or Apple Calendar"
              />
              <button onClick={copyFeed} className="text-xs font-bold text-teal-600 dark:text-teal-400 hover:underline flex-shrink-0">
                {copied ? 'Copied!' : 'Copy iCal link'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicklerPanel;
